import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, X, Search, Shield, Mail, 
  Phone, Building2, UserPlus, Trash2, Edit2,
  CheckCircle, XCircle, AlertCircle, AlertTriangle, Menu
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import api from '../utils/api';
import { fetchNotifications } from '../store/slices/notificationSlice';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const normalizeUserData = (userData) => {
    return {
        id: userData.id,
        email: userData.email,
        user_type: userData.user_type,
        status: userData.status,
        profile: {
            first_name: userData.profile.first_name,
            last_name: userData.profile.last_name,
            phone_number: userData.profile.phone_number,
            phone_extension: userData.profile.phone_extension
        },
        last_login: userData.last_login,
        created_at: userData.created_at,
        is_active: userData.is_active
    };
};

const canModifyUser = (currentUser, targetUser) => {
  // System admins can modify anyone
  if (currentUser.user_type === 'system_admin') return true;
  
  // Customer admins can't modify other admins
  if (targetUser.user_type === 'customer_admin') return false;
  
  // Can't modify self
  if (targetUser.id === currentUser.id) return false;
  
  return true;
};

const UserManagement = () => {
  const currentUser = useSelector((state) => state.auth.user);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Form state
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    phoneExtension: '',
    userType: 'customer',
    role: ''
  });

  // Roles available for customer organization
  const AVAILABLE_ROLES = [
    { value: 'customer', label: 'Regular User', description: 'Can create and manage loads' },
    { value: 'customer_admin', label: 'Admin', description: 'Full access including user management' }
  ];

  const fetchUsers = async () => {
    try {
        setIsLoading(true);
        const response = await api.get('/api/company/users');
        const normalizedUsers = response.data.map(normalizeUserData);
        setUsers(normalizedUsers);
        setFilteredUsers(normalizedUsers);
    } catch (error) {
        toast.error('Failed to fetch users');
        console.error('Error fetching users:', error);
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => {
        if (!user || !user.profile) return false;
        
        const email = (user.email || '').toLowerCase();
        const firstName = (user.profile.first_name || '').toLowerCase();
        const lastName = (user.profile.last_name || '').toLowerCase();
        const searchTerm = searchQuery.toLowerCase();
        
        return email.includes(searchTerm) ||
               firstName.includes(searchTerm) ||
               lastName.includes(searchTerm);
    });
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    if (!emailRegex.test(newUser.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!newUser.firstName || !newUser.lastName) {
      toast.error('First and last name are required');
      return false;
    }

    if (newUser.phoneNumber && !phoneRegex.test(newUser.phoneNumber)) {
      toast.error('Please enter a valid phone number');
      return false;
    }

    if (!newUser.role) {
      toast.error('Please select a role');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
        let response;
        if (selectedUser) {
            // Update existing user
            response = await api.put(`/api/customer/users/${selectedUser.id}`, newUser);
            const updatedUser = normalizeUserData(response.data.user);
            setUsers(prev => prev.map(user => 
                user.id === selectedUser.id ? updatedUser : user
            ));
            toast.success(response.data.message || 'User updated successfully');
        } else {
            // Create new user
            response = await api.post('/api/customer/users', newUser);
            const newUserData = normalizeUserData(response.data.user);
            setUsers(prev => [...prev, newUserData]);
            toast.success(response.data.message || 'User invited successfully');
        }
        
        setIsModalOpen(false);
        setNewUser({
            email: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            phoneExtension: '',
            userType: 'customer',
            role: ''
        });
        setSelectedUser(null);
    } catch (error) {
        const errorMessage = error.response?.data?.error || 
            `Failed to ${selectedUser ? 'update' : 'create'} user`;
        toast.error(errorMessage);
    }
  };

  const handleDeleteUser = (user) => {
    if (!canModifyUser(currentUser, user)) {
      toast.warning(
        user.id === currentUser.id
          ? "Administrators cannot delete their own account"
          : "Only system administrators can modify admin accounts",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }
    
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/customer/users/${userToDelete.id}`);
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      toast.info('User removed successfully', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to remove user', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleEditUser = (user) => {
    if (!canModifyUser(currentUser, user)) {
      toast.warning(
        user.id === currentUser.id
          ? "Administrators cannot modify their own account"
          : "Only system administrators can modify admin accounts",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }
    
    setSelectedUser(user);
    setNewUser({
      email: user.email,
      firstName: user.profile.first_name,
      lastName: user.profile.last_name,
      phoneNumber: user.profile.phone_number,
      phoneExtension: user.profile.phone_extension || '',
      userType: user.user_type,
      role: user.user_type
    });
    setIsModalOpen(true);
  };

  const UserStatusBadge = ({ status }) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: AlertCircle
      },
      confirmed: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        icon: XCircle
      }
    };

    const config = statusConfig[status.toLowerCase()];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const getDefaultCountry = (phoneNumber) => {
    // If phone starts with +1, it's US/Canada
    if (phoneNumber?.startsWith('+1')) return 'us';
    // If phone starts with +, try to extract country code
    if (phoneNumber?.startsWith('+')) {
      // This is a simplified approach - in production, you'd use a proper library
      // to determine the country from the dial code
      return 'us'; // Default fallback
    }
    return 'us'; // Default to US for backward compatibility
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return 'N/A';
    
    // Handle E.164 format (starting with '+')
    if (phone.startsWith('+')) {
      try {
        if (phone.startsWith('+1') && phone.length === 12) {
          // US/Canada format: +1 (XXX) XXX-XXXX
          return `+1 (${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8)}`;
        }
        // For other countries, add spaces for readability
        return phone.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{4})/, '$1 $2 $3 $4');
      } catch (e) {
        return phone; // Fall back to raw format
      }
    }
    
    // Legacy format (no +), assume US
    if (phone.length === 10) {
      return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
    }
    
    // Return as-is for other formats
    return phone;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <SideNavigation 
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
      />

      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          {(isMobile || isTablet) && (
            <button
              onClick={toggleSidebar}
              className="mb-4 p-2 bg-white rounded-md shadow-md"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}

          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <div className="bg-blue-50 rounded-lg p-2 mr-4">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                  <p className="text-sm text-gray-500">Manage user access and permissions</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setSelectedUser(null);
                  setNewUser({
                    email: '',
                    firstName: '',
                    lastName: '',
                    phoneNumber: '',
                    phoneExtension: '',
                    userType: 'customer',
                    role: ''
                  });
                  setIsModalOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Invite User
              </motion.button>
            </div>
          </div>

          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchQuery ? 'Try adjusting your search' : 'Get started by inviting a new user'}
                  </p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <li key={user.id}>
                    <div className="px-3 py-3 sm:px-4 sm:py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm sm:text-base text-blue-600 font-medium">
                                {user.profile.first_name[0]}{user.profile.last_name[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3 sm:ml-4">
                            <div className="flex flex-col sm:flex-row sm:items-center">
                              <h3 className="text-sm font-medium text-gray-900">
                                {user.profile.first_name} {user.profile.last_name}
                              </h3>
                              {user.user_type === 'customer_admin' && (
                                <span className="mt-1 sm:mt-0 sm:ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Admin
                                </span>
                              )}
                            </div>
                            <div className="mt-1 text-xs sm:text-sm text-gray-500">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                                <span className="flex items-center">
                                  <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                  {user.email}
                                </span>
                                {user.profile.phone_number && (
                                  <span className="flex items-center mt-1 sm:mt-0">
                                    <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                    {formatPhoneForDisplay(user.profile.phone_number)}
                                    {user.profile.phone_extension && (
                                      <span className="ml-1 text-gray-400">ext. {user.profile.phone_extension}</span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-3 sm:mt-0 sm:ml-4">
                          <UserStatusBadge status={user.status} />
                          <div className="flex items-center space-x-2">
                            {!canModifyUser(currentUser, user) ? (
                              <span className="text-xs text-gray-500 italic">
                                {user.id === currentUser.id ? "Current Admin" : "Admin Account"}
                              </span>
                            ) : (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEditUser(user)}
                                  className="p-1 rounded-full hover:bg-gray-100"
                                >
                                  <Edit2 className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleDeleteUser(user)}
                                  className="p-1 rounded-full hover:bg-gray-100"
                                >
                                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                                </motion.button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </main>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="relative bg-white rounded-lg mx-auto my-8 max-w-2xl"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedUser ? 'Edit User' : 'Invite New User'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={newUser.firstName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={newUser.lastName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={newUser.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <PhoneInput
                        country={getDefaultCountry(newUser.phoneNumber)}
                        value={newUser.phoneNumber}
                        onChange={(value) => {
                          // Ensure the value starts with + for E.164 format
                          const formattedNumber = value.startsWith('+') ? value : '+' + value;
                          setNewUser({...newUser, phoneNumber: formattedNumber});
                        }}
                        inputClass="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        containerClass="mt-1 w-full"
                        enableSearch={true}
                        disableSearchIcon={true}
                        searchPlaceholder="Search country..."
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">Include country code for international numbers</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Extension <span className="text-gray-400">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        name="phoneExtension"
                        value={newUser.phoneExtension}
                        onChange={handleInputChange}
                        placeholder="e.g. 101"
                        maxLength={10}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">For shared org numbers</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <div className="mt-2 space-y-4">
                      {AVAILABLE_ROLES.map((role) => (
                        <div
                          key={role.value}
                          className={`relative flex items-start p-4 cursor-pointer rounded-lg border ${
                            newUser.role === role.value
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => handleInputChange({ target: { name: 'role', value: role.value } })}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name="role"
                                value={role.value}
                                checked={newUser.role === role.value}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <label className="ml-3 font-medium text-gray-900">
                                {role.label}
                              </label>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              {role.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {selectedUser ? 'Save Changes' : 'Send Invitation'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isDeleteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Deletion
              </h3>
              
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to remove{' '}
                <span className="font-medium text-gray-700">
                  {userToDelete?.profile.first_name} {userToDelete?.profile.last_name}
                </span>
                ? This action cannot be undone.
              </p>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setUserToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer 
        position={isMobile ? "bottom-center" : "top-right"}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default UserManagement; 